import { Head, Link, router, useForm } from '@inertiajs/react'
import { Trash2, TriangleAlert } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { ScriptText } from '@/components/script-text'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { adminProductions, adminScenes } from '@/routes'
import type { Production } from '@/types'

type Props = {
  production: Production
  scene: { id: number | null; act: string | null; title: string }
  text: string
  castNames: string[]
}

type Preview = {
  characters: string[]
  lines: { kind: string; character: string | null; text: string }[]
  warning: string | null
}

export default function EditScene({ production, scene, text, castNames }: Props) {
  const isNew = scene.id === null
  const form = useForm({ scene: { act: scene.act ?? '', title: scene.title }, text })
  const [preview, setPreview] = useState<Preview | null>(null)
  const textarea = useRef<HTMLTextAreaElement>(null)
  const route = isNew
    ? adminScenes.create({ productionId: production.slug })
    : adminScenes.update({ productionId: production.slug, id: scene.id as number })

  const save = () => form.submit(route, { onSuccess: () => form.setDefaults() })

  // Re-parse as you type so speaker detection problems show before saving.
  useEffect(() => {
    if (!form.data.text.trim()) return setPreview(null)
    const controller = new AbortController()
    const timer = setTimeout(async () => {
      const csrf = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ''
      const res = await fetch(adminScenes.preview({ productionId: production.slug }).url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
        body: JSON.stringify({ text: form.data.text }),
        signal: controller.signal,
      }).catch(() => null)
      if (res?.ok) setPreview(await res.json())
    }, 300)
    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [form.data.text, production.slug])

  // ⌘S / Ctrl+S saves; leaving with unsaved changes asks first.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault()
        save()
      }
    }
    window.addEventListener('keydown', onKey)
    const offBefore = router.on('before', (event) => {
      const leaving = event.detail.visit.method === 'get'
      if (leaving && form.isDirty && !window.confirm('Discard unsaved changes to this scene?')) event.preventDefault()
    })
    return () => {
      window.removeEventListener('keydown', onKey)
      offBefore()
    }
  })

  /**
   * Start a new paragraph (a speech or a direction) after the one the cursor
   * is in — never inside it, so a verse speech isn't split and a direction's
   * closing parenthesis stays put. Leaves the cursor ready to type.
   */
  function insert(before: string, after = '') {
    const el = textarea.current
    const value = form.data.text
    const lines = value.split('\n')
    // Which line the cursor is on, then walk to the end of that speech.
    let line = value.slice(0, el?.selectionEnd ?? value.length).split('\n').length - 1
    const startsParagraph = (l: string) => l.trim() === '' || /^\(/.test(l.trim()) || /^[A-Z][A-Z0-9 .'-]*:/.test(l)
    while (line + 1 < lines.length && !startsParagraph(lines[line + 1] ?? '')) line++

    const head = lines
      .slice(0, line + 1)
      .join('\n')
      .replace(/\s*$/, '')
    const tail = lines
      .slice(line + 1)
      .join('\n')
      .replace(/^\s*/, '')
    const lead = head ? '\n\n' : ''
    const next = `${head}${lead}${before}${after}${tail ? `\n\n${tail}` : ''}`
    const cursor = head.length + lead.length + before.length
    // Update the DOM and caret synchronously, before React re-renders, so
    // keys typed straight after the click land at the new cursor.
    if (el) {
      el.value = next
      el.setSelectionRange(cursor, cursor)
      el.focus()
    }
    form.setData('text', next)
  }

  // Toolbar buttons must not take focus: the cursor stays in the script, and
  // a space typed right after clicking can't "press" the button again.
  const keepTyping = (e: React.MouseEvent) => e.preventDefault()

  const names = [...new Set([...castNames, ...(preview?.characters ?? [])])]

  return (
    <div className="grid gap-6">
      <Head title={`${isNew ? 'New scene' : scene.title} · ${production.title}`} />
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <Link
            href={
              isNew
                ? adminProductions.show(production.slug)
                : adminScenes.show({ productionId: production.slug, id: scene.id as number })
            }
            className="text-sm text-muted-foreground hover:underline"
          >
            ← {isNew ? production.title : `${production.title} · ${scene.title}`}
          </Link>
          <h1 className="text-2xl font-semibold">{isNew ? 'New scene' : 'Edit scene'}</h1>
        </div>
        <div className="flex gap-2">
          {!isNew && (
            <Button
              variant="outline"
              onClick={() => {
                if (window.confirm(`Delete “${scene.title}” and all its lines and audio?`)) {
                  form.setDefaults()
                  router.visit(adminScenes.destroy({ productionId: production.slug, id: scene.id as number }))
                }
              }}
            >
              <Trash2 /> Delete
            </Button>
          )}
          <Button onClick={save} disabled={form.processing || !form.data.scene.title.trim()}>
            {form.processing ? 'Saving…' : 'Save scene'}
            <kbd className="ml-1 text-xs opacity-60">⌘S</kbd>
          </Button>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-[12rem_1fr]">
        <div className="grid gap-2">
          <Label htmlFor="act">Act</Label>
          <Input
            id="act"
            placeholder="Act I"
            value={form.data.scene.act}
            onChange={(e) => form.setData('scene.act', e.target.value)}
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="title">Scene</Label>
          <Input
            id="title"
            placeholder="Scene 2. Capulet's orchard."
            value={form.data.scene.title}
            onChange={(e) => form.setData('scene.title', e.target.value)}
            required
          />
          {form.errors['scene.title'] && <p className="text-sm text-destructive">{form.errors['scene.title']}</p>}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="grid content-start gap-3">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="mr-1 text-xs text-muted-foreground">Insert:</span>
            {names.map((name) => (
              <Button
                key={name}
                size="sm"
                variant="secondary"
                onMouseDown={keepTyping}
                onClick={() => insert(`${name}: `)}
              >
                {name}
              </Button>
            ))}
            <Button size="sm" variant="outline" onMouseDown={keepTyping} onClick={() => insert('(', ')')}>
              (direction)
            </Button>
          </div>
          <Textarea
            ref={textarea}
            className="min-h-[32rem] font-mono text-sm leading-relaxed"
            value={form.data.text}
            onChange={(e) => form.setData('text', e.target.value)}
            placeholder={
              '(JULIET appears above at a window.)\n\nROMEO: But, soft! what light through yonder window breaks?\nIt is the east, and Juliet is the sun.\n\nJULIET: Ay me!'
            }
            spellCheck
          />
          <p className="text-xs text-muted-foreground">
            A speaker in CAPS and a colon starts a speech; following lines belong to it (keep verse line breaks).
            Anything in (parentheses) on its own line is a stage direction. New names become new characters.
          </p>
        </div>

        <Card className="self-start">
          <CardHeader>
            <CardTitle>Preview</CardTitle>
            <CardDescription>
              {preview ? `${preview.lines.length} lines` : 'What the cast will see, as you type.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="grid max-h-[36rem] gap-2 overflow-y-auto">
            {preview?.warning && (
              <Alert>
                <TriangleAlert />
                <AlertDescription>{preview.warning}</AlertDescription>
              </Alert>
            )}
            {preview?.lines.map((line, i) =>
              line.kind === 'direction' ? (
                // biome-ignore lint/suspicious/noArrayIndexKey: preview lines have no ids
                <p key={i} className="text-sm italic text-muted-foreground">
                  ({line.text})
                </p>
              ) : (
                // biome-ignore lint/suspicious/noArrayIndexKey: preview lines have no ids
                <div key={i} className="grid gap-0.5">
                  <span className="flex items-center gap-1.5 text-xs font-semibold tracking-wide">
                    {line.character}
                    {line.character && !castNames.includes(line.character) && (
                      <Badge variant="outline" className="h-4 px-1 text-[10px]">
                        new character
                      </Badge>
                    )}
                  </span>
                  <p className="whitespace-pre-line text-sm">
                    <ScriptText text={line.text} />
                  </p>
                </div>
              ),
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
